import { z } from 'zod';
// Schema for the handoff workflow selection response
export const HandoffSelectionSchema = z.object({
    selectedIndex: z
        .number()
        .min(0)
        .describe('Zero-based index of the selected workflow from the provided list'),
    selectedWorkflowId: z
        .string()
        .describe('The ID of the selected workflow for verification'),
    confidence: z.number().min(0).max(1).describe('Confidence score from 0 to 1'),
    selectionReason: z
        .string()
        .describe('Clear explanation of why this workflow was selected as most appropriate'),
});
// Schema for action group selection response
export const ActionGroupSelectionSchema = z.object({
    selectedIndex: z
        .number()
        .min(0)
        .describe('Zero-based index of the selected action group from the provided list'),
    selectedActionGroupName: z
        .string()
        .describe('The name of the selected action group for verification'),
    confidence: z.number().min(0).max(1).describe('Confidence score from 0 to 1'),
    selectionReason: z
        .string()
        .describe('Clear explanation of why this action group was selected as most appropriate'),
});
export const handoffWorkflowSelectionTemplate = {
    name: 'handoff-workflow-selection',
    version: '1.0.1',
    responseSchema: HandoffSelectionSchema,
    variableSchema: z.object({
        conversationTopics: z.string().describe('Conversation Topics'),
        transferIndividuals: z.string().describe('Transfer Individuals'),
        transferDepartments: z.string().describe('Transfer Departments'),
        completionReason: z.string().describe('Completion Reason'),
        primaryCategory: z.string().describe('Primary Category'),
        workflowOptions: z.string().describe('Workflow Options'),
        sessionProperties: z.string().describe('Session Properties'),
    }),
    outputHighlights: {
        fields: [
            { key: 'selectedWorkflowId', label: 'Selected Workflow' },
            { key: 'selectionReason', label: 'Reason' },
        ],
    },
    evalFields: [
        {
            key: 'selectedWorkflowId',
            type: 'text',
            extract: r => r.selectedWorkflowId,
        },
        {
            key: 'confidence',
            type: 'number',
            extract: r => r.confidence,
        },
    ],
    systemPrompt: `You are an expert call routing analyst. Your role is to select the most appropriate handoff workflow based on call analysis.

CRITICAL INSTRUCTIONS:
1. Review the extracted call metadata to understand the caller's intent and needs
2. Compare the extracted details against each workflow's selection criteria
3. Select the workflow that best matches the caller's specific request
4. Provide the zero-based index (0, 1, 2, etc.) of your selected workflow
5. Include the workflow ID for verification
6. Explain your reasoning clearly and concisely

EMERGENCY PRIORITY:
- If conversation topics indicate an emergency (fire, flooding, gas leak, safety threat), MUST prioritize emergency workflows

SELECTION CRITERIA:
- EXACT MATCH: When call content directly matches workflow criteria (confidence 0.8-1.0)
- SEMANTIC MATCH: When call intent aligns with workflow purpose (confidence 0.6-0.8)
- PARTIAL MATCH: When some elements match but not perfectly (confidence 0.4-0.6)
- DEFAULT FALLBACK: When no clear match exists (confidence 0.2-0.4)

WORKFLOW SELECTION PRIORITY:
If multiple workflows could match, select the most specific/relevant one based on:
- Property-specific workflows: If a property is identified in the call, prioritize handoff workflows filtered to that specific property over company-wide "All properties" workflows
- Direct mention of person/department to transfer to in the call
- Semantic alignment between caller intent and workflow criteria
- Specificity of the match (exact person > department > general)
- As last resort: General company-wide or "default"/"fallback" workflows

IMPORTANT: Always return a valid index number that corresponds to a workflow in the provided list.`,
    userPrompt: `CALL ANALYSIS:
Conversation Topics: {{conversationTopics}}
Transfer Individuals: {{transferIndividuals}}
Transfer Departments: {{transferDepartments}}
Call Completion Reason: {{completionReason}}
Primary Category: {{primaryCategory}}

CONTACT'S PROPERTIES (CRM relationships or mentioned in conversation):
{{sessionProperties}}

AVAILABLE WORKFLOWS (with zero-based index):
{{workflowOptions}}

TASK: Select the most appropriate workflow from AVAILABLE WORKFLOWS. When selected, provide the following information from the selection:
1. selectedIndex: The zero-based Workflow Index number (i.e. "Workflow Index: 0" returns 0)
2. selectedWorkflowId: The workflow ID for verification
3. confidence: Score from 0-1 based on match quality
4. selectionReason: Clear explanation of your choice

Focus on matching the caller's specific needs to the workflow's selection criteria.`,
    variables: [
        'conversationTopics',
        'transferIndividuals',
        'transferDepartments',
        'completionReason',
        'primaryCategory',
        'workflowOptions',
        'sessionProperties',
    ],
    metadata: {
        model: 'gpt-5.4-nano',
        temperature: 0.1,
        maxTokens: 400,
        responseFormat: 'json_object',
    },
};
export const actionGroupSelectionTemplate = {
    name: 'action-group-selection',
    version: '1.0.1',
    responseSchema: ActionGroupSelectionSchema,
    variableSchema: z.object({
        conversationTopics: z.string().describe('Conversation Topics'),
        transferIndividuals: z.string().describe('Transfer Individuals'),
        transferDepartments: z.string().describe('Transfer Departments'),
        completionReason: z.string().describe('Completion Reason'),
        workflowName: z.string().describe('Workflow Name'),
        workflowCriteria: z.string().describe('Workflow Criteria'),
        actionGroupOptions: z.string().describe('Action Group Options'),
    }),
    outputHighlights: {
        fields: [
            { key: 'selectedActionGroupName', label: 'Selected Action Group' },
            { key: 'selectionReason', label: 'Reason' },
        ],
    },
    evalFields: [
        {
            key: 'selectedActionGroupName',
            type: 'text',
            extract: r => r.selectedActionGroupName,
        },
        {
            key: 'confidence',
            type: 'number',
            extract: r => r.confidence,
        },
    ],
    systemPrompt: `You are an expert action routing specialist. Your role is to select the most appropriate action group within a handoff workflow.

CRITICAL INSTRUCTIONS:
1. Analyze the call context to identify specific people, roles, or departments mentioned
2. Match the caller's request against the "When" conditions in each action group's selection criteria
3. Select the action group that most precisely matches the call routing intent
4. Provide the zero-based index (0, 1, 2, etc.) of your selected action group
5. Include the action group name for verification
6. Explain your reasoning with specific references to the call content

EMERGENCY PRIORITY:
- If conversation topics indicate an emergency (fire, flooding, gas leak, safety threat), prioritize emergency-related action groups

SELECTION PRIORITY:
- SPECIFIC PERSON: When transferIndividuals exists for a named individual and is not empty or "None" (highest priority)
- SPECIFIC ROLE: When transferDepartments exists for a particular job title or department
- GENERAL CATEGORY: When conversationTopics matches an action group's criteria
- DEFAULT: When no specific criteria are met

PROPERTY CONTEXT AWARENESS:
- The workflow was already selected based on property matching
- Action groups within this workflow are property-aware
- Prioritize action groups that align with the caller's property context
- If specific property-related personnel are mentioned as transferIndividuals, prefer those action groups

IMPORTANT: Always return a valid index number that corresponds to an action group in the provided list.`,
    userPrompt: `CALL ANALYSIS:
Conversation Topics: {{conversationTopics}}
Transfer Individuals: {{transferIndividuals}}
Transfer Departments: {{transferDepartments}}
Call Completion Reason: {{completionReason}}

SELECTED WORKFLOW CONTEXT:
Workflow Name: {{workflowName}}
Workflow Selection Criteria: {{workflowCriteria}}

AVAILABLE ACTION GROUPS (with zero-based index):
{{actionGroupOptions}}

TASK: Select the most appropriate action group by providing:
1. selectedIndex: The zero-based index number (0, 1, 2, etc.)
2. selectedActionGroupName: The action group name for verification
3. confidence: Score from 0-1 based on match specificity
4. selectionReason: Clear explanation referencing specific call details

Match the caller's specific request to the most appropriate action group criteria.`,
    variables: [
        'conversationTopics',
        'transferIndividuals',
        'transferDepartments',
        'completionReason',
        'workflowName',
        'workflowCriteria',
        'actionGroupOptions',
    ],
    metadata: {
        model: 'gpt-5.4-nano',
        temperature: 0.1,
        maxTokens: 300,
        responseFormat: 'json_object',
    },
};
export const smsHandoffWorkflowSelectionTemplate = {
    name: 'sms-handoff-workflow-selection',
    version: '1.3.0',
    variableSchema: z.object({
        conversationTopics: z.string().describe('Conversation Topics'),
        transferIndividuals: z.string().describe('Transfer Individuals'),
        transferDepartments: z.string().describe('Transfer Departments'),
        escalationReason: z.string().describe('Escalation Reason'),
        contactGroup: z.string().describe('Contact Group'),
        requiresFollowUp: z.string().describe('Requires Follow-Up'),
        primaryCategory: z.string().describe('Primary Category'),
        workflowOptions: z.string().describe('Workflow Options'),
        sessionProperties: z.string().describe('Session Properties'),
        sessionContactGroups: z.string().describe('Session Contact Groups'),
    }),
    outputHighlights: {
        fields: [
            { key: 'selectedWorkflowId', label: 'Selected Workflow' },
            { key: 'selectionReason', label: 'Reason' },
        ],
    },
    systemPrompt: `You are an expert SMS routing analyst. Your role is to select the most appropriate handoff workflow based on SMS conversation analysis.

CRITICAL INSTRUCTIONS:
1. Review the extracted conversation metadata to understand the contact's intent and needs
2. "Transfer Individuals" and "Transfer Departments" reflect what the AI assistant confirmed for routing or handoff—not merely what the customer asked for. Weight them accordingly when matching workflows.
3. Compare the extracted details against each workflow's selection criteria
4. Select the workflow that best matches the contact's specific request
5. Provide the zero-based index (0, 1, 2, etc.) of your selected workflow
6. Include the workflow ID for verification
7. Explain your reasoning clearly and concisely

EMERGENCY PRIORITY:
- If conversation topics indicate an emergency (fire, flooding, gas leak, safety threat), MUST prioritize emergency workflows
- Only select non-emergency workflows if no emergency option exists

SELECTION CRITERIA:
- EXACT MATCH: When conversation content directly matches workflow criteria (confidence 0.8-1.0)
- SEMANTIC MATCH: When conversation intent aligns with workflow purpose (confidence 0.6-0.8)
- PARTIAL MATCH: When some elements match but not perfectly (confidence 0.4-0.6)
- DEFAULT FALLBACK: When no clear match exists (confidence 0.2-0.4)

WORKFLOW SELECTION PRIORITY:
If multiple workflows could match, select the most specific/relevant one based on:
- Property-specific workflows: If a property is identified in sessionProperties, always favor the matching property-specific handoff workflows filtered to over company-wide "All properties" workflows. Only select an All properties workflow when there is no appropriately matching property-specific workflow and an All properties workflow is indeed a better match.
- Intent matches a person/department name in transferIndividuals or transferDepartments
- Semantic alignment between conversationTopics and workflow criteria
- Specificity of the match (exact person > department > general)
- As last resort: General company-wide or "default"/"fallback" workflows

IMPORTANT: Always return a valid index number that corresponds to a workflow in the provided list.`,
    userPrompt: `SMS CONVERSATION ANALYSIS:
Conversation Topics: {{conversationTopics}}
Transfer Individuals (assistant-confirmed): {{transferIndividuals}}
Transfer Departments (assistant-confirmed): {{transferDepartments}}
Escalation Reason: {{escalationReason}}
Contact Group: {{contactGroup}}
Requires Follow-Up: {{requiresFollowUp}}
Primary Category: {{primaryCategory}}

CONTACT'S PROPERTIES (CRM relationship or mentioned in conversation):
{{sessionProperties}}

CONTACT'S GROUP MEMBERSHIPS (relationship types detected):
{{sessionContactGroups}}

AVAILABLE WORKFLOWS (with zero-based index):
{{workflowOptions}}

TASK: Select the most appropriate workflow by providing:
1. selectedIndex: The zero-based index number (0, 1, 2, etc.)
2. selectedWorkflowId: The workflow ID for verification
3. confidence: Score from 0-1 based on match quality
4. selectionReason: Clear explanation of your choice

Focus on matching the contact's specific needs to the workflow's selection criteria.`,
    variables: [
        'conversationTopics',
        'transferIndividuals',
        'transferDepartments',
        'escalationReason',
        'contactGroup',
        'requiresFollowUp',
        'primaryCategory',
        'workflowOptions',
        'sessionProperties',
        'sessionContactGroups',
    ],
    metadata: {
        model: 'gpt-5.4-nano',
        temperature: 0.1,
        maxTokens: 400,
        responseFormat: 'json_object',
    },
};
// SMS-specific action group selection template
export const smsActionGroupSelectionTemplate = {
    name: 'sms-action-group-selection',
    version: '1.0.1',
    variableSchema: z.object({
        conversationTopics: z.string().describe('Conversation Topics'),
        transferIndividuals: z.string().describe('Transfer Individuals'),
        transferDepartments: z.string().describe('Transfer Departments'),
        escalationReason: z.string().describe('Escalation Reason'),
        workflowName: z.string().describe('Workflow Name'),
        workflowCriteria: z.string().describe('Workflow Criteria'),
        actionGroupOptions: z.string().describe('Action Group Options'),
    }),
    outputHighlights: {
        fields: [
            { key: 'selectedActionGroupName', label: 'Selected Action Group' },
            { key: 'selectionReason', label: 'Reason' },
        ],
    },
    systemPrompt: `You are an expert action routing specialist. Your role is to select the most appropriate action group within an SMS handoff workflow.

CRITICAL INSTRUCTIONS:
1. Analyze the SMS conversation context to identify specific people, roles, or departments mentioned
2. Match the contact's request against the "When" conditions in each action group's selection criteria
3. Select the action group that most precisely matches the contact's specific request
4. Provide the zero-based index (0, 1, 2, etc.) of your selected action group
5. Include the action group name for verification
6. Explain your reasoning with specific references to the conversation content

EMERGENCY PRIORITY:
- If conversation topics indicate an emergency (fire, flooding, gas leak, safety threat), prioritize emergency-related action groups

SELECTION PRIORITY:

- SPECIFIC PERSON: When transferIndividuals contains one or more extracted named individuals (highest priority); do not use this priority when it is empty or "None"
- SPECIFIC ROLE: When transferDepartments identifies a particular job title or department
- GENERAL CATEGORY: When conversationTopics matches an action group's criteria
- DEFAULT: When no specific criteria are met

IMPORTANT: Always return a valid index number that corresponds to an action group in the provided list.`,
    userPrompt: `SMS CONVERSATION ANALYSIS:
Conversation Topics: {{conversationTopics}}
Transfer Individuals (assistant-confirmed): {{transferIndividuals}}
Transfer Departments (assistant-confirmed): {{transferDepartments}}
Escalation Reason: {{escalationReason}}

SELECTED WORKFLOW CONTEXT:
Workflow Name: {{workflowName}}
Workflow Selection Criteria: {{workflowCriteria}}

AVAILABLE ACTION GROUPS (with zero-based index):
{{actionGroupOptions}}

TASK: Select the most appropriate action group by providing:
1. selectedIndex: The zero-based index number (0, 1, 2, etc.)
2. selectedActionGroupName: The action group name for verification
3. confidence: Score from 0-1 based on match specificity
4. selectionReason: Clear explanation referencing specific conversation details

Match the contact's specific request to the most appropriate action group criteria.`,
    variables: [
        'conversationTopics',
        'transferIndividuals',
        'transferDepartments',
        'escalationReason',
        'workflowName',
        'workflowCriteria',
        'actionGroupOptions',
    ],
    metadata: {
        model: 'gpt-5.4-nano',
        temperature: 0.1,
        maxTokens: 300,
        responseFormat: 'json_object',
    },
};
//# sourceMappingURL=handoff-selection.js.map